import { BaseProcessor, ColumnMetaData } from './BaseProcessor';
import * as Highcharts from 'highcharts';

export class LineChart extends BaseProcessor {
  protected override chartType = 'line';

  constructor(
    data: { [key: string]: any }[],
    xAxis: string[],
    yAxis: string[],
    groupBy: string[],
    columnsMetaData: { [key: string]: ColumnMetaData } = {},
    options: any = {}
  ) {
    super();
    this.data = data;
    this.xAxis = xAxis;
    this.yAxis = yAxis;
    this.groupBy = groupBy;
    this.columnsMetaData = columnsMetaData;
    this.options = options;
    this.addChartTypeToOptions();
  }

  private processGroupBy() {
    const groupByCol = this.groupBy[0];
    const xAxisCol = this.xAxis[0];
    const yAxisCol = this.yAxis[0];

    // Group data by the specified column
    const groupedData: { [key: string]: any[] } = {};

    this.data.forEach((values: any) => {
      const groupKey = values[groupByCol];
      if (!groupedData[groupKey]) {
        groupedData[groupKey] = [];
      }
      let xValue = this.formateValueWithType(
        values[xAxisCol],
        this.columnsMetaData[xAxisCol]
      );
      groupedData[groupKey].push({
        x: xValue,
        y: this.formateValueWithType(
          values[yAxisCol],
          this.columnsMetaData[yAxisCol]
        ),
      });
    });

    // Convert grouped data to series
    const series = Object.entries(groupedData).map(([name, data]) => {
      // Sort data points by x-value
      const sortedData = data.sort((a, b) => a.x - b.x);
      return {
        name: name,
        yMetaData: this.columnsMetaData[yAxisCol],
        xMetaData: this.columnsMetaData[xAxisCol],
        data: sortedData.map((point) => [point.x, point.y]),
      };
    });

    // Update chart options
    this.options.series = series;

    return this.options;
  }

  addXAxisData() {
    if (this.xAxis.length > 0) {
      if (this.isDateTime(this.columnsMetaData[this.xAxis[0]].type)) {
        // If x-axis values are datetime, set xAxis type to 'datetime'
        this.options.xAxis.type = 'datetime';
        // Set xAxis label format based on datatype
        const xAxisMeta = this.columnsMetaData[this.xAxis[0]];
        if (xAxisMeta?.type === 'datetime') {
          this.options.xAxis.labels = {
            format: '{value:%B %d %Y}',
          };
        } else if (xAxisMeta?.type === 'year') {
          this.options.xAxis.labels = {
            formatter: function () {
              // Show only the year part
              const date = new Date(this.value);
              return date.getFullYear();
            },
          };
        } else if (xAxisMeta?.type === 'month') {
          this.options.xAxis.labels = {
            formatter: function () {
              // Show as "Jan,2025"
              const date = new Date(this.value);
              const month = date.toLocaleString('default', { month: 'short' });
              const year = date.getFullYear();
              return `${month},${year}`;
            },
          };
        } else {
          this.options.xAxis.labels = {
            format: '{value}',
          };
        }
      }
    }
  }

  addToolTip() {
    if (this.groupBy.length) {
      if (
        this.xAxis.length > 0 &&
        this.isDateTime(this.columnsMetaData[this.xAxis[0]].type)
      ) {
        this.options.tooltip = {
          shared: true,
          crosshairs: true,
          formatter: function (this: any) {
            const points = this.points || [];
            let tooltipString = `<b>Date: ${this.x}</b><br/>`;
            let xMetaData = this.point.series.userOptions.xMetaData;
            let yMetaData = this.point.series.userOptions.yMetaData;
            if (xMetaData.type === 'datetime' || xMetaData.type === 'date') {
              const formattedDate = Highcharts.dateFormat('%Y-%m-%d', this.x);
              tooltipString = `<b>Date: ${formattedDate}</b><br/>`;
            } else if (xMetaData.type === 'month') {
              const formattedDate = Highcharts.dateFormat('%b %Y', this.x);
              tooltipString = `<b>Month : ${formattedDate}</b><br/>`;
            } else if (xMetaData.type === 'year') {
              const formattedDate = Highcharts.dateFormat('%Y', this.x);
              tooltipString = `<b>Year : ${formattedDate}</b><br/>`;
            }
            let yUnit = yMetaData?.unit;
            points.forEach((point: any) => {
              if (typeof point.y === 'number') {
                tooltipString += `${point.series.name}: <b> ${
                  yUnit ? yUnit : ''
                }${point.y.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}</b><br/>`;
              } else {
                tooltipString += `${point.series.name}: ${point.y}<br/>`;
              }
            });
            return tooltipString;
          },
        };
      } else {
        // Enhanced tooltip for grouped line charts
        this.options.tooltip = {
          shared: true,
          crosshairs: true,
          formatter: function (this: any) {
            const points = this.points || [];
            let yMetaData = this.point.series.userOptions.yMetaData;
            let tooltipString = `<b>${this.x}</b><br/>`;

            points.forEach((point: any) => {
              tooltipString += `${point.series.name}: ${
                yMetaData?.unit || ''
              }${point.y.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}<br/>`;
            });
            return tooltipString;
          },
        };
      }
    } else {
      this.options.tooltip = {
        headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
        pointFormatter: function () {
          // Format point.y with commas as thousands separator
          const points = this.points || [];
          let yMetaData = this.series.userOptions.yMetaData;
          const formattedY =
            typeof this.y === 'number'
              ? this.y.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : this.y;
          return `<span style="color:${this.color}">\u25CF</span> ${
            this.series.name
          }: <b>${yMetaData?.unit || ''}${formattedY}</b><br/>`;
        },
      };
    }
  }

  preprocessData(): any {
    delete this.options.series; // Clear previous series data
    delete this.options.data;
    // add plotOptions
    // add series data
    if (this.groupBy?.length) {
      this.processGroupBy();

      this.addToolTip();
    } else {
      let xCol = this.xAxis[0];
      // sort data by x-axis values
      this.data.sort((a: any, b: any) => {
        const aValue = this.formateValueWithType(
          a[xCol],
          this.columnsMetaData[xCol]
        );
        const bValue = this.formateValueWithType(
          b[xCol],
          this.columnsMetaData[xCol]
        );
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      });

      this.options.series = [
        ...this.yAxis.map((yCol: string) => {
          return {
            name: yCol,
            yMetaData: this.columnsMetaData[yCol],
            xMetaData: this.columnsMetaData[xCol],
            data: this.data.map((value: any) => {
              return [
                this.formateValueWithType(
                  value[xCol],
                  this.columnsMetaData[xCol]
                ),
                this.formateValueWithType(
                  value[yCol],
                  this.columnsMetaData[yCol]
                ),
              ];
            }),
          };
        }),
      ];
    }
    this.addXAxisTitle();
    this.addYAxisTitle();
    this.addXAxisData();
    this.addToolTip();

    // add xAxis title & yAxis title
  }
}
